// src/components/chatbot/Chatbot.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Segment,
  Header,
  Icon,
  Comment,
  Form,
  TextArea,
  Button,
  Message,
  Label,
  Modal,
  Divider
} from "semantic-ui-react";
import { withTranslation } from "react-i18next";
import ServicesMap from "./ServicesMap";
import { authHeader, authHeaderSet, anonHeader } from '../../helpers/auth-header.js';

const API_ENVIRONMENTS = {
  dev: {
    label: "DEV",
    baseUrl: "https://dev.api.zipster.care",
    authMode: "keycloak",
  },
  acc: {
    label: "ACC",
    baseUrl: "https://acc.api.zipster.care",
    authMode: "basic",
    basicUsername: "marcvanbree",
    basicPassword: "password",
  },
};

const DEFAULT_API_ENVIRONMENT = "dev";

const basicAuthHeader = ({ username, password }) => {
  if (!username || !password) return {};
  return { Authorization: `Basic ${btoa(`${username}:${password}`)}` };
};

// kind: "chat" | "feedback" | "hal" | "json"
const makeHeadersFor = ({ anonymous, kind, environmentConfig }) => {
  const env = environmentConfig || API_ENVIRONMENTS[DEFAULT_API_ENVIRONMENT];

  let base = {};

  if (env.authMode === "basic") {
    // ACC/local test flow: always Basic auth. Do not deploy this with hardcoded credentials.
    base = basicAuthHeader({
      username: env.basicUsername,
      password: env.basicPassword,
    });
  } else if (anonymous) {
    // DEV anonymous flow: keep existing anonHeader behavior.
    base = anonHeader();
  } else {
    // DEV authenticated flow: keep existing Keycloak behavior.
    base = kind === "hal" ? authHeaderSet() : authHeader();
  }

  if (kind === "hal") {
    return {
      ...base,
      Accept: "application/hal+json, application/json",
    };
  }

  return {
    ...base,
    Accept: "application/json, application/problem+json, text/plain, */*",
  };
};

const DEFAULT_API_BASE_URL = API_ENVIRONMENTS[DEFAULT_API_ENVIRONMENT].baseUrl;

const buildDefaultEndpoints = (baseUrl) => ({
  chatbot: `${baseUrl}/SPP/administration/v1/chatbot/complete-text`,
  chatbotAnon: `${baseUrl}/SPP/administration/v1/chatbot/anonymous/complete-text`,
  feedback: `${baseUrl}/SPP/administration/v1/chatbot/feedback`,
  healthcareServices: `${baseUrl}/SPP/administration/v1/healthcareservices`,
  conceptMappings: `${baseUrl}/SPP/administration/v1/chatbot/chatbotcodableconceptmappings`,
  serviceMappings: `${baseUrl}/SPP/administration/v1/chatbot/chatbothealthcareservicemappings`,
  addresses: `${baseUrl}/SPP/administration/v1/addresses`,
  mailableResources: `${baseUrl}/SPP/administration/v1/mailableresources`,
});

/** -----------------------------
 *  Markdown helpers + parser (tolerant)
 *  ----------------------------- */

const stripMd = (s = "") =>
  String(s)
    .replace(/\*\*/g, "")
    .replace(/`/g, "")
    .trim();

const formatInline = (text) => {
  if (!text) return "";
  let result = String(text);
  // **bold**
  result = result.replace(/\*\*(.+?)\*\*/g, "$1");
  // links: [text](https://...)
  result = result.replace(
    /\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g,
    "$1 ($2)"
  );
  return result;
};

const looksLikeZipsterServiceMarkdown = (raw = "") =>
  /###\s+/.test(raw) &&
  /(Type:|Beschrijving:|Contact:|Provider:|Locatie:)/i.test(raw);

const pickField = (text, key) => {
  const re = new RegExp(`${key}\\s*:\\s*([^\\n]+)`, "i");
  const m = String(text).match(re);
  if (!m) return "";
  return m[1].split(/\s+-\s+\w+\s*:/)[0].trim();
};

const parseServicesFromMarkdown = (raw = "") => {
  if (!raw) return { intro: "", services: [] };

  const normalized = String(raw)
    .replace(/\s*---\s*/g, "\n---\n")
    .replace(/###\s*/g, "\n### ");

  let intro = "";
  const firstHeadingIdx = normalized.indexOf("\n### ");
  if (firstHeadingIdx > -1) intro = normalized.slice(0, firstHeadingIdx).trim();
  else if (!normalized.trim().startsWith("###")) intro = normalized.trim();

  const parts = normalized.split(/\n###\s+/).filter(Boolean);

  const services = [];
  for (const part of parts) {
    const name = stripMd(part.split(/\s+-\s+/)[0].split(/\r?\n/)[0]);
    if (!name) continue;

    const svcText = part;

    services.push({
      name,
      type: pickField(svcText, "Type"),
      provider: pickField(svcText, "Provider"),
      description: pickField(svcText, "Beschrijving"),
      doelgroep: pickField(svcText, "Doelgroep"),
      location: pickField(svcText, "Locatie"),
      phone: pickField(svcText, "Telefoon") || pickField(svcText, "Tel"),
      email: pickField(svcText, "E-mail") || pickField(svcText, "Email"),
      website: pickField(svcText, "Website")
    });
  }

  return { intro, services };
};

/** -----------------------------
 *  NIEUW: Mapping helpers (tolerant)
 *  ----------------------------- */

const ensureHttp = (url) => {
  if (!url) return "";
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const extractConceptMappingId = (cm) => {
  // meest waarschijnlijk: backend geeft een id veld terug
  const direct =
    cm?.externalId || // 
    cm?.chatbotCodableConceptMappingId ||
    cm?.chatbotCodableConceptMappingID ||
    cm?.id ||
    cm?.mappingId ||
    cm?.uuid;


  if (direct) return String(direct);

  // soms zit het genest (bv. cm.mapping.id of cm.chatbotCodableConceptMapping.id)
  const nested =
    cm?.mapping?.id ||
    cm?.mapping?.uuid ||
    cm?.chatbotCodableConceptMapping?.id ||
    cm?.chatbotCodableConceptMapping?.uuid;

  if (nested) return String(nested);

  return "";
};

/** -----------------------------
 *  Description extraction helper
 *  ----------------------------- */
const extractDescriptionText = (raw) => {
  if (!raw) return "";

  // Als het al een object is (soms backend-gedrag)
  if (typeof raw === "object" && raw.description) {
    return String(raw.description).trim();
  }

  if (typeof raw !== "string") {
    return String(raw);
  }

  const trimmed = raw.trim();

  // Snelle check: lijkt het JSON?
  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed?.description) {
        return String(parsed.description).trim();
      }
    } catch (e) {
      // JSON kapot → val terug op originele tekst
      return trimmed;
    }
  }

  return trimmed;
};

const normalizeService = (svc) => {
  if (!svc) return null;

  const name = svc.name || "";
  const type =
    svc?.serviceType?.name ||
    svc?.type?.name ||
    svc?.type ||
    svc?.category ||
    "";

  const provider =
    svc?.organisation?.name ||
    svc?.organization?.name ||
    svc?.provider?.name ||
    svc?.provider ||
    "";

  const description =
    extractDescriptionText(svc.description) ||
    extractDescriptionText(svc.comment) ||
    extractDescriptionText(svc.summary) ||
    "";

  const phone =
    svc.phone ||
    svc.telephone ||
    "";

  const email =
    svc.email ||
    "";

  const website =
    svc.website ||
    svc.url ||
    // Check for mailable resources with name "Website"
    (svc.mailableResources && Array.isArray(svc.mailableResources) 
      ? svc.mailableResources.find(r => r.name === "Website")?.url 
      : "") ||
    "";

  const a = svc?.addressDetails || svc?.address || null;

  const location =
    (Array.isArray(a?.lines) ? a.lines.join(", ") : a?.lines || "") ||
    [a?.postalCode, a?.city].filter(Boolean).join(" ") ||
    svc?.address?.city ||
    svc?.address?.municipality ||
    "";

  return {
    uuid: svc.uuid,
    name,
    type,
    provider,
    description,
    location,
    phone,
    email,
    website,
    __raw: svc
  };
};

/** -----------------------------
 *  Component
 *  ----------------------------- */

function Chatbot({ t, anonymous = false, apiBaseUrl = "", endpoints = {} }) {
  const [selectedEnvironment, setSelectedEnvironment] = useState(DEFAULT_API_ENVIRONMENT);

  const environmentConfig = useMemo(() => {
    return API_ENVIRONMENTS[selectedEnvironment] || API_ENVIRONMENTS[DEFAULT_API_ENVIRONMENT];
  }, [selectedEnvironment]);

  const EP = useMemo(() => {
    const base = apiBaseUrl || environmentConfig.baseUrl || DEFAULT_API_BASE_URL;
    return { ...buildDefaultEndpoints(base), ...endpoints };
  }, [apiBaseUrl, environmentConfig, endpoints]);

  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 720);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Add CSS animation for the bouncing dots activity indicator
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes bounce {
        0%, 60%, 100% {
          transform: translateY(0);
        }
        30% {
          transform: translateY(-10px);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const makeWelcomeMessages = () => [
    {
      id: 1,
      from: "bot",
      text: "Hallo 👋 Waarmee kan ik je helpen?",
      timestamp: new Date()
    }
  ];

  const [messages, setMessages] = useState(makeWelcomeMessages());
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [hoveredSuggestedIndex, setHoveredSuggestedIndex] = useState(null);

  // Nieuwe API: session mag leeg zijn voor eerste bericht, daarna sessionIdentifier UUID
  const sessionRef = useRef(""); // "" => backend maakt nieuwe sessie

  const scrollContainerRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Modal voor service-details
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Modal voor feedback op bot-bericht
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackTarget, setFeedbackTarget] = useState(null); // bot message object
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackResult, setFeedbackResult] = useState({ ok: false, msg: "" });
  const [feedbackSubmittedFor, setFeedbackSubmittedFor] = useState({});
  const [activeFeedbackHistoryId, setActiveFeedbackHistoryId] = useState(null);

  // Modal voor kaart (optie B)
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapModalServices, setMapModalServices] = useState([]);
  const [hoveredMapIndex, setHoveredMapIndex] = useState(null);

  // Google Maps key (fallbacks)
  const mapsKey = "AIzaSyAqE43ZJgJp2moxHZmaWq49YSuaONCau0s";

  const openMapModal = (services = []) => {
    setMapModalServices(Array.isArray(services) ? services : []);
    setMapModalOpen(true);
  };

  const closeMapModal = () => {
    setMapModalOpen(false);
    setMapModalServices([]);
    setHoveredMapIndex(null);
  };

  const submitFeedback = async () => {
    if (!feedbackText.trim() || !activeFeedbackHistoryId) return;

    setFeedbackSubmitting(true);
    setFeedbackResult({ ok: false, msg: "" });
    try {
      const resp = await fetch(EP.feedback, {
        method: "POST",
        headers: {
          ...makeHeadersFor({ anonymous, kind: "feedback", environmentConfig }),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          feedback: feedbackText.trim(),
          history: activeFeedbackHistoryId
        })
      });

      if (!resp.ok) throw new Error(`Feedback fout (HTTP ${resp.status})`);

      // markeer feedback als verstuurd (voor UI: enkel "Sluiten")
      setFeedbackSubmittedFor((prev) => ({
        ...prev,
        [activeFeedbackHistoryId]: true
      }));

      // (optioneel) markeer ook het botbericht zelf als "feedbackGiven"
      setMessages((prev) =>
        prev.map((m) =>
          m.chatHistoryIdentifier === activeFeedbackHistoryId
            ? { ...m, feedbackGiven: true }
            : m
        )
      );

      setFeedbackResult({ ok: true, msg: "Dankjewel! Feedback is opgeslagen." });
    } catch (e) {
      setFeedbackResult({
        ok: false,
        msg: e.message || "Er ging iets fout bij het versturen van feedback."
      });
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const openServiceModal = (svc) => {
    setSelectedService(svc);
    setServiceModalOpen(true);
  };

  const closeServiceModal = () => {
    setServiceModalOpen(false);
    setSelectedService(null);
  };

  const openFeedbackModal = (botMsg) => {
    setFeedbackTarget(botMsg);
    setActiveFeedbackHistoryId(botMsg.chatHistoryIdentifier); // 👈 DIT
    setFeedbackText("");
    setFeedbackResult({ ok: false, msg: "" });
    setFeedbackModalOpen(true);
  };

  const closeFeedbackModal = () => {
    if (feedbackSubmitting) return;
    setFeedbackModalOpen(false);
    setFeedbackTarget(null);
    setActiveFeedbackHistoryId(null); // 👈 belangrijk
    setFeedbackText("");
    setFeedbackResult({ ok: false, msg: "" });
  };

  // Alleen auto-scrollen als autoScroll === true → scroll in de container zelf
  useEffect(() => {
    if (!autoScroll) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [messages, autoScroll]);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    setAutoScroll(distanceFromBottom < 40);
  };

  const startNewChat = () => {
    sessionRef.current = "";
    setError("");
    setSending(false);
    setInput("");
    setAutoScroll(true);
    setMessages(makeWelcomeMessages());

    // sluit modals netjes
    setServiceModalOpen(false);
    setSelectedService(null);
    closeFeedbackModal();
  };

  const normalizeNameKey = (s = "") =>
    String(s)
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/[’'"]/g, "")
      .replace(/[^\p{L}\p{N}\s-]/gu, "") // punctuation weg, unicode-safe
      .trim();

  const fetchChatbotReply = async (text) => {
    const endpointToUse = anonymous ? EP.chatbotAnon : EP.chatbot;

    const resp = await fetch(endpointToUse, {
      method: "POST",
      headers: {
        ...makeHeadersFor({ anonymous, kind: "chat", environmentConfig }),
        "Content-Type": "application/json",
        Accept: "text/plain, application/json, application/problem+json, */*",
      },
      body: JSON.stringify({
        session: sessionRef.current || "",
        message: text
      })
    });

    const raw = await resp.text().catch(() => "");

    if (!resp.ok) {
      // raw is hier vaak problem+json
      throw new Error(`Chatbot fout (HTTP ${resp.status}) ${raw}`);
    }

    // ✅ Succes kan plain text zijn
    const ct = resp.headers.get("content-type") || "";
    let data = null;

    if (ct.includes("application/json") || ct.includes("application/problem+json")) {
      data = raw ? JSON.parse(raw) : {};
    } else {
      data = { response: raw };
    }

    if (data.sessionIdentifier && !sessionRef.current) {
      sessionRef.current = data.sessionIdentifier;
    }

    const reply =
      data.response ||
      data.reply ||
      data.message ||
      data.output ||
      data.output_text ||
      data.text;

    return {
      reply: reply || raw || "Leeg antwoord.",
      sessionIdentifier: data.sessionIdentifier || sessionRef.current || "",
      chatHistoryIdentifier: data.chatHistoryIdentifier || ""
    };
  };

  /** -----------------------------
   *  NIEUW: Services ophalen via mappings
   *  ----------------------------- */

  const fetchConceptMappings = async (chatHistoryId) => {
    const url = `${EP.conceptMappings}?chatbotHistoryId=${encodeURIComponent(
      chatHistoryId
    )}`;

    const resp = await fetch(url, {
      method: "GET",
      headers: makeHeadersFor({ anonymous, kind: "hal", environmentConfig }),
    });

    if (!resp.ok) {
      const txt = await resp.text().catch(() => "");
      throw new Error(
        `Concept mappings fout (HTTP ${resp.status}) ${txt ? `- ${txt}` : ""}`
      );
    }

    const data = await resp.json().catch(() => null);
    if (!data) return [];

    // Wees extra tolerant op wrapper-structuren
    const arr =
      (Array.isArray(data) && data) ||
      data?._embedded?.chatbotCodableConceptMappings || // ✅ HIER
      data.items ||
      data.mappings ||
      data.results ||
      data.data ||
      [];

    return Array.isArray(arr) ? arr : [];
  };

  const fetchServiceMappings = async (conceptMappingId) => {
    let url = `${EP.serviceMappings}?chatbotCodableConceptMappingId=${encodeURIComponent(
      conceptMappingId
    )}&pagesize=50&page=1`;

    const all = [];

    while (url) {
      const resp = await fetch(url, {
        method: "GET",
        headers: makeHeadersFor({ anonymous, kind: "hal", environmentConfig }),
      });

      if (!resp.ok) {
        const txt = await resp.text().catch(() => "");
        throw new Error(
          `Service mappings fout (HTTP ${resp.status}) ${txt ? `- ${txt}` : ""}`
        );
      }

      const data = await resp.json().catch(() => null);
      if (!data) break;

      const arr =
        (Array.isArray(data) && data) ||
        data?._embedded?.chatbotHealthcareServiceMappings ||
        data.items ||
        data.mappings ||
        data.results ||
        data.data ||
        [];

      if (Array.isArray(arr)) all.push(...arr);

      // HAL next-link
      url = data?._links?.next?.href || null;
    }

    return all;
  };

  const fetchSuggestedServicesForHistory = async (chatHistoryId) => {
    if (!chatHistoryId) return [];

    const conceptMappings = await fetchConceptMappings(chatHistoryId);

    // 👇 DEBUG: kijk of er ids in zitten
    const mappingIds = conceptMappings
      .map(extractConceptMappingId)
      .filter(Boolean);

    console.log("🧩 conceptMappings count:", conceptMappings.length);
    console.log("🧩 extracted mappingIds:", mappingIds);

    if (mappingIds.length === 0) {
      console.warn(
        "⚠️ Geen chatbotCodableConceptMappingId gevonden in concept mappings response:",
        conceptMappings
      );
      return [];
    }

    // Parallel ophalen
    const serviceLists = await Promise.all(
      mappingIds.map(async (id) => {
        try {
          const serviceMappings = await fetchServiceMappings(id);

          // haal uuids uit mapping
          const uuids = serviceMappings
            .map((m) => m?.healthcareService?.uuid)
            .filter(Boolean);

          // parallel details ophalen (robust)
          const detailResults = await Promise.allSettled(
            uuids.map((u) => fetchHealthcareService(u))
          );

          const detailedServices = detailResults
            .filter((r) => r.status === "fulfilled")
            .map((r) => normalizeService(r.value))
            .filter(Boolean);

          return detailedServices;
        } catch (e) {
          console.warn("⚠️ fetchServiceMappings faalde voor id:", id, e);
          return [];
        }
      })
    );

    const allServices = serviceLists.flat();

    // dedupe light (op name+provider+location)
    const seen = new Set();
    return allServices.filter((s) => {
      const key = `${s.name}||${s.provider}||${s.location}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;

    setError("");

    const userText = input.trim();
    const userMessage = {
      id: Date.now(),
      from: "user",
      text: userText,
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setSending(true);

    try {
      const { reply, chatHistoryIdentifier } = await fetchChatbotReply(userText);

      // NIEUW: suggested services ophalen en attachen aan bot message
      let suggestedServices = [];
      if (chatHistoryIdentifier) {
        try {
          suggestedServices = await fetchSuggestedServicesForHistory(
            chatHistoryIdentifier
          );
        } catch (e) {
          console.warn("Kon suggesties niet ophalen:", e);
        }
      }

      const botMessage = {
        id: Date.now() + 1,
        from: "bot",
        text: reply,
        timestamp: new Date(),
        // bewaren voor feedback-koppeling
        chatHistoryIdentifier: chatHistoryIdentifier || "",
        // NIEUW: servicesuggesties om zelf te renderen
        suggestedServices
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (e) {
      setError(e.message || "Er ging iets fout met de chatbot.");
    } finally {
      setSending(false);
    }
  };



  const renderInlineWithItalics = (text) => {
    if (!text) return null;

    const parts = [];
    let lastIndex = 0;
    const regex = /\*(?!\*)([^*]+)\*/g; // match *italic*, maar niet **bold**

    let match;
    while ((match = regex.exec(text)) !== null) {
      // tekst vóór italics
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      // italic stuk
      parts.push(
        <em key={match.index} style={{ opacity: 0.85 }}>
          {match[1]}
        </em>
      );

      lastIndex = match.index + match[0].length;
    }

    // resttekst
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  function ServiceCard({ svc, idx, t, onOpen, onHoverIndex }) {
    const [hover, setHover] = useState(false);

    const hasAnyContact = Boolean(svc?.phone || svc?.email || svc?.website);
    const hasLocation = Boolean(svc?.location);
    const typeLabel = svc?.type || "";

    // description is already processed in normalizeService
    const descriptionText = svc?.description || "";

    const handleEnter = () => {
      setHover(true);
      if (typeof onHoverIndex === "function") onHoverIndex(idx);
    };

    const handleLeave = () => {
      setHover(false);
      if (typeof onHoverIndex === "function") onHoverIndex(null);
    };

    return (
      <Segment
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={() => onOpen(svc)}
        style={{
          margin: 0,                 // ✅ BELANGRIJK (Semantic UI heeft default margin)
          borderRadius: 14,
          padding: 0,
          background: "#fff",
          border: "1px solid #e6e9ef",
          boxShadow: hover
            ? "0 10px 24px rgba(17,24,39,0.10)"
            : "0 4px 12px rgba(17,24,39,0.06)",
          transform: hover ? "translateY(-1px)" : "translateY(0)",
          transition: "box-shadow 160ms ease, transform 160ms ease",
          overflow: "hidden",
          position: "relative",
          // Mobile-friendly sizing
          minWidth: "100%",
          maxWidth: "100%",
          cursor: "pointer" // Show pointer cursor to indicate clickability
        }}
      >
        {/* accent strip */}
        <div
          style={{
            width: 6,
            background: "#FBA213",
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0
          }}
        />

        <div style={{ padding: "10px 10px 10px 14px" }}>
          {/* top row */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 10,
              minHeight: 40 // 🔑 cruciaal - reduced for mobile
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icon
                  name={hasLocation ? "map marker alternate" : "info circle"}
                  style={{ opacity: 0.6, margin: 0, fontSize: "0.9rem" }}
                />
                <div
                  style={{
                    fontWeight: 800,
                    color: "#111827",
                    fontSize: 14, // Reduced for mobile
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    lineHeight: 1.2,
                    maxHeight: 32 // Reduced for mobile
                  }}
                  title={svc?.name}
                >
                  {svc?.name || t("Dienst")}
                </div>
              </div>

              {/* subline */}
              <div
                style={{
                  marginTop: 6,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                  alignItems: "center"
                }}
              >
                {typeLabel ? (
                  <span
                    style={{
                      fontSize: 12,
                      padding: "3px 8px",
                      borderRadius: 999,
                      background: "#f3f4f6",
                      color: "#374151",
                      fontWeight: 600
                    }}
                  >
                    {typeLabel}
                  </span>
                ) : null}

                {svc?.location ? (
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    {svc.location}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {/* description */}
          {descriptionText ? (
            <div
              style={{
                marginTop: 10,
                fontSize: 13,
                color: "#374151",
                lineHeight: 1.35,
                whiteSpace: "pre-line",
                maxHeight: 72,
                overflow: "hidden"
              }}
            >
              {descriptionText}
            </div>
          ) : null}

          {/* footer row */}
          <div
            style={{
              marginTop: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10
            }}
          >
            {hasAnyContact ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  color: "#1f7a1f",
                  fontWeight: 700
                }}
              >
                <Icon name="check circle" /> {t("Contact beschikbaar")}
              </span>
            ) : (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  color: "#6b7280",
                  fontWeight: 600
                }}
              >
                <Icon name="info circle" /> {t("Geen contactinfo")}
              </span>
            )}

            {/* Meer info button - moved to bottom right */}
            <Button
              size="small"
              basic
              onClick={(e) => {
                e.stopPropagation();
                onOpen(svc);
              }}
              style={{
                borderRadius: 10,
                fontWeight: 700,
                borderColor: "#e6e9ef",
                color: "#111827",
                whiteSpace: "nowrap",
                padding: "6px 12px",
                fontSize: "0.85rem"
              }}
            >
              {t("Meer info")}{" "}
              <Icon
                name="chevron right"
                style={{ marginLeft: 6, opacity: 0.6 }}
              />
            </Button>
          </div>
        </div>
      </Segment>
    );
  }

  const renderServiceCard = (svc, idx) => (
    <ServiceCard
      key={`${svc.uuid || svc.name || "dienst"}-${idx}`}
      svc={svc}
      idx={idx}
      t={t}
      onOpen={openServiceModal}
    />
  );

  const fetchHealthcareService = async (serviceUuid) => {
    if (!serviceUuid) return null;

    const url = `${EP.healthcareServices}/${encodeURIComponent(serviceUuid)}`;

    const resp = await fetch(url, {
      method: "GET",
      headers: {
        ...makeHeadersFor({ anonymous, kind: "json", environmentConfig }),
        Accept: "application/json",
      },
    });

    if (!resp.ok) {
      const txt = await resp.text().catch(() => "");
      throw new Error(`HealthcareService fout (HTTP ${resp.status}) ${txt || ""}`);
    }

    const svc = await resp.json().catch(() => null);
    if (!svc) return null;

    // ✅ address is een reference → fetch details
    const addressUuid = svc?.address?.uuid;
    if (addressUuid) {
      try {
        const aResp = await fetch(
          `${EP.addresses}/${encodeURIComponent(addressUuid)}`,
          {
            method: "GET",
            headers: {
              ...makeHeadersFor({ anonymous, kind: "json", environmentConfig }),
              Accept: "application/json",
            },
          }
        );
        if (aResp.ok) {
          const addressDetails = await aResp.json().catch(() => null);
          // hang het aan de service zodat ServicesMap het kan gebruiken
          svc.addressDetails = addressDetails || null;
        }
      } catch (e) {
        // stil falen: kaartjes blijven werken, alleen map/geocode minder
        console.warn("Adres details ophalen faalde:", e);
      }
    }

    // ✅ fetch mailable resources separately
    try {
      const mResp = await fetch(
        `${EP.mailableResources}?healthcareService=${encodeURIComponent(serviceUuid)}`,
        {
          method: "GET",
          headers: {
            ...makeHeadersFor({ anonymous, kind: "json", environmentConfig }),
            Accept: "application/json",
          },
        }
      );
      if (mResp.ok) {
        const mailableResourcesData = await mResp.json().catch(() => null);
        // extract the embedded resources
        svc.mailableResources = mailableResourcesData?._embedded?.mailableResources || [];
      }
    } catch (e) {
      // stil falen: website link blijft gewoon leeg
      console.warn("Mailable resources ophalen faalde:", e);
    }

    return svc;
  };

  const extractServiceNamesFromText = (raw = "") => {
    const text = String(raw);

    // Match "### Naam" ook als alles op 1 lijn staat.
    // We stoppen op: " -", newline, of volgende heading "###"
    const re = /###\s*([^#\n\r]+?)(?=\s+-|\n|###|$)/g;

    const names = [];
    let m;
    while ((m = re.exec(text)) !== null) {
      const name = stripMd(m[1] || "").trim();
      if (name) names.push(name);
    }

    return names;
  };

  const nameMatchesAllowed = (serviceName = "", allowedNameKeys) => {
    if (!allowedNameKeys || allowedNameKeys.size === 0) return true;

    const sKey = normalizeNameKey(serviceName);
    if (!sKey) return false;

    // exact match
    if (allowedNameKeys.has(sKey)) return true;

    // tolerant match (substring beide richtingen)
    for (const a of allowedNameKeys) {
      if (!a) continue;
      if (sKey.includes(a) || a.includes(sKey)) return true;
    }

    return false;
  };

  const renderBotContent = (rawText, msg) => {
    const suggested = Array.isArray(msg?.suggestedServices) ? msg.suggestedServices : [];

    // Bepaal "allowed" service-namen uit de bot-tekst (robust)
    let allowedNameKeys = null;

    if (rawText && /###\s*/.test(String(rawText))) {
      let parsedNames = [];

      if (looksLikeZipsterServiceMarkdown(rawText)) {
        const { services: parsedServices } = parseServicesFromMarkdown(rawText);
        parsedNames = (parsedServices || []).map((s) => s?.name).filter(Boolean);
      }

      if (!parsedNames.length) {
        parsedNames = extractServiceNamesFromText(rawText);
      }

      if (parsedNames.length) {
        allowedNameKeys = new Set(parsedNames.map((n) => normalizeNameKey(n)).filter(Boolean));
      }
    }

    // 1) Als we suggested services hebben: toon ENKEL kaarten (geen bottekst)
    if (suggested.length > 0) {
      const filteredSuggested = allowedNameKeys
        ? suggested.filter((svc) =>
          nameMatchesAllowed(svc?.name || "", allowedNameKeys)
        )
        : suggested;

      const toShow = filteredSuggested.length ? filteredSuggested : suggested;

      return (
        <div>
          <div style={{ marginBottom: 10, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, color: "#111827" }}>
                {t("Op basis van wat je vertelde, vond ik deze plekken in de buurt")}
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                {t("Klik op een kaartje voor meer info.")}
              </div>
            </div>

            <Button
              basic
              size="small"
              icon
              labelPosition="left"
              onClick={() => openMapModal(toShow)}
              disabled={!toShow?.length}
              style={{ whiteSpace: "nowrap" }}
            >
              <Icon name="map outline" />
              {t("Bekijk op kaart")}
            </Button>
          </div>

          {/* Grid layout for desktop (better overview), simple list for mobile */}
          <div
            style={{
              display: "grid",
              gap: 16,
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              alignItems: "start",
              // On mobile, show simpler layout
              "@media (max-width: 767px)": {
                gridTemplateColumns: "1fr",
                gap: 12
              }
            }}
          >
            {toShow.map((svc, idx) => (
              <div
                key={`${svc?.uuid || svc?.name || "svc"}-${idx}`}
                style={{
                  // On mobile, show simplified card
                  "@media (max-width: 767px)": {
                    cursor: "pointer"
                  }
                }}
                onClick={() => {
                  // On mobile, clicking card opens modal
                  if (window.innerWidth <= 767) {
                    openServiceModal(svc);  // Use the correct function name
                  }
                }}
              >
                {renderServiceCard(svc, idx)}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // 2) Geen suggestedServices: als de tekst service-markdown bevat → toon kaarten uit tekst (en ook geen ruwe tekst)
    if (looksLikeZipsterServiceMarkdown(rawText)) {
      const { services: parsedServices } = parseServicesFromMarkdown(rawText);
      return (
        <div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700, color: "#111827" }}>
              {t("Enkele opties die je kan bekijken")}
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
              {t("Klik op een kaartje voor meer info.")}
            </div>
          </div>
          {/* Grid layout for desktop (better overview), simple list for mobile */}
          <div
            style={{
              display: "grid",
              gap: 10,
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              // On mobile, show simpler layout
              "@media (max-width: 767px)": {
                gridTemplateColumns: "1fr",
                gap: 8
              }
            }}
          >
            {(parsedServices || []).map((svc, idx) => (
              <div
                key={`${svc?.name || "parsed-svc"}-${idx}`}
                style={{
                  // On mobile, show simplified card
                  "@media (max-width: 767px)": {
                    cursor: "pointer"
                  }
                }}
                onClick={() => {
                  // On mobile, clicking card opens modal
                  if (window.innerWidth <= 767) {
                    openServiceModal(svc);  // Use the correct function name
                  }
                }}
              >
                {renderServiceCard(svc, idx)}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // 3) Default: gewone tekst
    if (!rawText) return null;
    return <span> {renderInlineWithItalics(formatInline(rawText))}</span>;
  };

  const renderMessage = (msg) => {
    const isUser = msg.from === "user";
    const hasMap = !isUser && Array.isArray(msg?.suggestedServices) && msg.suggestedServices.length > 0;
    const content = isUser ? msg.text : renderBotContent(msg.text, msg);

    const canGiveFeedback =
      !isUser && Boolean(msg.chatHistoryIdentifier) && !msg.feedbackGiven;

    return (
      <Comment key={msg.id} style={{ marginBottom: 12 }}>
        {/* Avatar - Mobile friendly */}
        {!isUser && (
          <Comment.Avatar
            src="/vivahub-symbol.png"
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "#fff",
              padding: 3,
              objectFit: "contain",
              boxShadow: "0 0 4px rgba(251,162,19,0.35)",
              marginTop: 2
            }}
          />
        )}

        <Comment.Content style={{ textAlign: isUser ? "right" : "left" }}>
          <Comment.Author as="span" style={{ fontSize: "0.9rem" }}>
            {isUser ? t("Jij") : t("Zippy")}
          </Comment.Author>

          <Comment.Metadata style={{ fontSize: 10, color: "#888" }}>
            <span>
              {msg.timestamp instanceof Date
                ? msg.timestamp.toLocaleTimeString()
                : msg.timestamp}
            </span>
          </Comment.Metadata>

          <Comment.Text>
            <Segment
              style={{
                display: hasMap ? "block" : "inline-block",
                width: hasMap ? "100%" : "auto",
                maxWidth: hasMap ? "100%" : "85%",
                borderRadius: 16,
                padding: "6px 10px",
                marginTop: 4,
                marginBottom: 4,
                wordBreak: "break-word",
                background: "#ffffff",
                color: "#111827",
                border: isUser ? "1px solid rgba(17,24,39,0.18)" : "1px solid #FBA213",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                fontSize: "0.9rem",
                lineHeight: "1.4"
              }}
            >
              {content}
            </Segment>

            {/* Feedback actions (alleen bij bot) - Mobile friendly */}
            {!isUser && (
              <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {canGiveFeedback ? (
                  <Button
                    basic
                    size="tiny"
                    icon
                    labelPosition="left"
                    onClick={() => openFeedbackModal(msg)}
                    style={{ padding: "4px 6px", fontSize: "0.8rem" }}
                  >
                    <Icon name="comment alternate outline" />
                    <span style={{ fontSize: "0.8rem" }}>{t("Feedback")}</span>
                  </Button>
                ) : null}

                {!isUser && msg.feedbackGiven ? (
                  <span style={{ fontSize: 11, color: "#2c7a2c", display: "flex", alignItems: "center" }}>
                    <Icon name="check" style={{ fontSize: "0.9rem" }} />
                    <span style={{ marginLeft: 4 }}>{t("Feedback ontvangen")}</span>
                  </span>
                ) : null}
              </div>
            )}
          </Comment.Text>
        </Comment.Content>
      </Comment >
    );
  };

  const modalWebsite = useMemo(() => {
    const w = selectedService?.website || "";
    if (!w) return "";
    return ensureHttp(w);
  }, [selectedService]);

  return (
    <div
      style={{
        background: "#f4f6f8",
        padding: "16px 8px"
      }}
    >
      <Container fluid>
        <div
          style={{
            maxWidth: 800,
            margin: "0 auto",
            background: "#ffffff",
            borderRadius: 16,
            boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
            border: "1px solid #e1e5ea",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            height: "90vh",
            width: "100%"
          }}
        >
          {/* Header - Mobile Friendly */}
          <Segment
            basic
            style={{
              margin: 0,
              padding: "8px 12px",
              borderBottom: "1px solid #e1e5ea",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              flexWrap: "wrap"
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <Header as="h3" style={{ margin: 0, fontSize: "1.2rem" }}>
                {t("Digitale VivaHub Gids")}
              </Header>
              <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                {t("Stel je vraag over welzijn, zorgen of dagelijkse problemen.")}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Button.Group size="tiny" basic>
                {Object.entries(API_ENVIRONMENTS).map(([key, env]) => (
                  <Button
                    key={key}
                    active={selectedEnvironment === key}
                    onClick={() => {
                      if (selectedEnvironment === key || sending) return;
                      setSelectedEnvironment(key);
                      startNewChat();
                    }}
                    disabled={sending}
                    style={{
                      fontWeight: selectedEnvironment === key ? 800 : 500,
                      background: selectedEnvironment === key ? "#fff4df" : undefined,
                    }}
                  >
                    {env.label}
                  </Button>
                ))}
              </Button.Group>

              <Label
                size="tiny"
                color={selectedEnvironment === "acc" ? "orange" : "green"}
                basic
                style={{ fontWeight: 500, padding: "4px 6px" }}
              >
                {t("Prototype")} · {environmentConfig.label}
              </Label>

              <Button
                basic
                size="tiny"
                icon
                labelPosition="left"
                onClick={startNewChat}
                disabled={sending}
                style={{ padding: "6px 8px" }}
              >
                <Icon name="redo" />
                <span style={{ fontSize: "0.8rem" }}>{t("Nieuwe chat")}</span>
              </Button>
            </div>
          </Segment>

          {/* Body: messages + input - Mobile Optimized */}
          <Segment
            basic
            style={{
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              position: "relative"
            }}
          >
            {/* Messages - Scrollable area */}
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "8px 12px 4px",
                WebkitOverflowScrolling: "touch" // Smooth scrolling on iOS
              }}
            >
              <Comment.Group style={{ maxWidth: "100%", margin: 0 }}>
                {messages.map(renderMessage)}

                {/* Enhanced Inline Activity Indicator - More prominent but inline */}
                {sending && (
                  <Comment>
                    <Comment.Avatar
                      src="/vivahub-symbol.png"
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: "#fff",
                        padding: 3,
                        objectFit: "contain",
                        boxShadow: "0 0 4px rgba(251,162,19,0.35)",
                        marginTop: 2
                      }}
                    />
                    <Comment.Content>
                      <Comment.Author as="span" style={{ fontSize: "0.9rem" }}>
                        {t("Zippy")}
                      </Comment.Author>
                      <Comment.Text>
                        <Segment
                          style={{
                            display: "inline-block",
                            borderRadius: 16,
                            padding: "8px 12px",
                            background: "#ffffff",
                            border: "1px solid #FBA213",
                            maxWidth: "85%",
                            fontSize: "0.9rem",
                            boxShadow: "0 2px 6px rgba(251,162,19,0.15)"
                          }}
                        >
                          {/* Animated dots indicator - inline */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              marginBottom: 4
                            }}
                          >
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "#FBA213",
                                animation: "bounce 1.4s infinite ease-in-out",
                                animationDelay: "0s"
                              }}
                            />
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "#FBA213",
                                animation: "bounce 1.4s infinite ease-in-out",
                                animationDelay: "0.2s"
                              }}
                            />
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "#FBA213",
                                animation: "bounce 1.4s infinite ease-in-out",
                                animationDelay: "0.4s"
                              }}
                            />
                            <span style={{ marginLeft: 8, opacity: 0.8 }}>
                              {t("Zippy denkt na…")}
                            </span>
                          </div>
                        </Segment>
                      </Comment.Text>
                    </Comment.Content>
                  </Comment>
                )}
              </Comment.Group>
            </div>

            {/* Footer: error + input - Mobile optimized */}
            <div
              style={{
                borderTop: "1px solid #e1e5ea",
                padding: "6px 8px",
                background: "#fafbfc"
              }}
            >
              {error && (
                <Message
                  negative
                  size="tiny"
                  style={{ marginBottom: 6, fontSize: "0.8rem" }}
                  content={error}
                />
              )}

              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!sending) sendMessage();
                }}
              >
                <Form.Field style={{ marginBottom: 4 }}>
                  <TextArea
                    placeholder={t("Typ hier wat jou bezighoudt…")}
                    value={input}
                    onChange={(e, { value }) => setInput(value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (!sending && input.trim()) sendMessage();
                      }
                    }}
                    rows={2}
                    style={{
                      resize: "none",
                      borderRadius: 8,
                      fontSize: "0.9rem",
                      padding: "8px 10px"
                    }}
                  />
                </Form.Field>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 4
                  }}
                >
                  <Button
                    primary
                    icon
                    labelPosition="right"
                    type="submit"
                    disabled={!input.trim() || sending}
                    size="small"
                    style={{ padding: "6px 12px", fontSize: "0.85rem" }}
                  >
                    {t("Verstuur")}
                    <Icon name="send" />
                  </Button>
                </div>
              </Form>
            </div>
          </Segment>
        </div>
      </Container>

      {/* Service details modal - Mobile friendly */}
      <Modal open={serviceModalOpen} onClose={closeServiceModal} size="small" closeIcon>
        <Modal.Header style={{ fontSize: "1.1rem" }}>{selectedService?.name || t("Dienst")}</Modal.Header>
        <Modal.Content>
          {selectedService ? (
            <div style={{ lineHeight: 1.5, fontSize: "0.9rem" }}>
              {selectedService.type ? (
                <p>
                  <strong>Type:</strong> {selectedService.type}
                </p>
              ) : null}

              {selectedService.provider ? (
                <p>
                  <strong>Provider:</strong> {selectedService.provider}
                </p>
              ) : null}

              {selectedService.location ? (
                <p>
                  <strong>Locatie:</strong> {selectedService.location}
                </p>
              ) : null}

              {selectedService.doelgroep ? (
                <p>
                  <strong>Doelgroep:</strong> {selectedService.doelgroep}
                </p>
              ) : null}

              {selectedService.description ? (
                <>
                  <Divider />
                  <p>
                    <strong>Beschrijving:</strong>
                    <br />
                    {selectedService.description}
                  </p>
                </>
              ) : null}

              <Divider />

              <Header as="h4">Contact</Header>

              {selectedService.phone ? (
                <p>
                  <strong>Telefoon:</strong> {selectedService.phone}
                </p>
              ) : null}

              {selectedService.email ? (
                <p>
                  <strong>E-mail:</strong> {selectedService.email}
                </p>
              ) : null}

              {modalWebsite ? (
                <p>
                  <strong>Website:</strong>{" "}
                  <a href={modalWebsite} target="_blank" rel="noreferrer">
                    {modalWebsite}
                  </a>
                </p>
              ) : null}

              {!selectedService.phone &&
                !selectedService.email &&
                !modalWebsite ? (
                <p style={{ color: "#777" }}>Geen contactgegevens beschikbaar.</p>
              ) : null}
            </div>
          ) : null}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeServiceModal}>{t("Sluiten")}</Button>
        </Modal.Actions>
      </Modal>

      {/* Feedback modal - Mobile friendly */}
      <Modal open={feedbackModalOpen} onClose={closeFeedbackModal} size="small" closeIcon>
        <Modal.Header>{t("Feedback op dit antwoord")}</Modal.Header>
        <Modal.Content>
          {feedbackTarget ? (
            <>
              <div style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>
                {t(
                  "Wat was er niet juist/nuttig? Waar had Zippy meer op moeten focussen?"
                )}
              </div>

              <Form>
                <Form.Field>
                  <TextArea
                    rows={3}
                    placeholder={t(
                      "Voorbeeld: “Ik had liever gehad dat je focuste op mijn verslavingsproblematiek i.p.v. …”"
                    )}
                    value={feedbackText}
                    onChange={(e, { value }) => setFeedbackText(value)}
                    style={{ resize: "vertical", borderRadius: 8, fontSize: "0.9rem" }}
                    disabled={feedbackSubmitting}
                  />
                </Form.Field>
              </Form>

              {feedbackResult.msg ? (
                <Message
                  positive={feedbackResult.ok}
                  negative={!feedbackResult.ok}
                  size="small"
                  content={feedbackResult.msg}
                />
              ) : null}

              {/* (debug/info) history id */}
              {!feedbackTarget.chatHistoryIdentifier ? (
                <Message
                  warning
                  size="small"
                  content="Dit antwoord heeft geen history-id (feedback kan niet gelinkt worden)."
                />
              ) : null}
            </>
          ) : null}
        </Modal.Content>
        <Modal.Actions>
          {feedbackSubmittedFor[activeFeedbackHistoryId] ? (
            <Button onClick={closeFeedbackModal}>Sluiten</Button>
          ) : (
            <>
              <Button basic onClick={closeFeedbackModal}>
                Annuleren
              </Button>
              <Button primary onClick={submitFeedback} disabled={feedbackSubmitting}>
                Verstuur feedback
              </Button>
            </>
          )}
        </Modal.Actions>
      </Modal>
      {/* Map modal (Optie B) */}
      <Modal open={mapModalOpen} onClose={closeMapModal} size="large" closeIcon>
        <Modal.Header>{t("Suggesties in de buurt")}</Modal.Header>

        <Modal.Content scrolling style={{ padding: 0 }}>
          {!mapsKey ? (
            <div style={{ padding: 16 }}>
              <Message
                warning
                content={t("Google Maps key ontbreekt (REACT_APP_GOOGLE_MAPS_KEY).")}
              />
            </div>
          ) : (
            <div
              style={{
                height: "70vh",
                minHeight: 520,
                width: "100%"
              }}
            >
              {/* Render de map enkel als modal open is -> minder load/gedoe */}
              <ServicesMap
                services={mapModalServices}
                onSelect={(svc) => openServiceModal(svc)}
                googleMapsApiKey={mapsKey}
              />
            </div>
          )}
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={closeMapModal}>{t("Sluiten")}</Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}

export default withTranslation()(Chatbot);